import { LitElement, html, nothing } from "lit";

import { customElement, query, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhArrowDownRight";
import "@phosphor-icons/webcomponents/PhArrowUpRight";
import Chart, { type ChartData } from "chart.js/auto";
import { DataQueryController } from "ui-common";
import "ui-components/button/wow-button";
import "ui-components/group-button/group-button";
import "ui-components/modal/wow-modal";

import { analyticsPageStyles } from "./analytics-page.css";

import "../../components/page-content/page-content";
import "../../components/subscriber-emails-modal/subscriber-emails-modal";
import { analyticsDataQueryOptions } from "../../queries/analytics-data";
import { t } from "../../translations/i18n";
import { getAssetUrl } from "helpers/assetHelpers";
import { platformConfigs } from "ui-common/socialMediaConfig";
import { appContext } from "ui-common/appContext";
import { navigateTo, router } from "../../router";

const RANGE_PRESETS = [
  { key: 7, label: "Last 7 days" },
  { key: 14, label: "Last 14 days" },
  { key: 30, label: "Last 30 days" },
  { key: "custom", label: "Custom" },
  { key: "custom-applied", label: "" },
] as const;

type RangeKey = 7 | 14 | 30 | "custom" | "custom-applied";

@customElement("analytics-page")
export class AnalyticsPage extends LitElement {
  @state() private activeTab: "links" | "posts" | "discounts" = "links";
  @state() private linksSubTab: "links" | "socials" = "links";
  @state() private range: RangeKey = 7;
  @state() private startDate!: Date;
  @state() private endDate!: Date;
  @state() private customAppliedLabel: string | null = null;

  @state() private isModalOpen = false;
  @state() private showSubscriberEmailsModal = false;
  @state() private showStatisticsModal = false;
  @state() private showFullScreenPostModal = false;
  @state() private selectedPost: {
    id: string;
    title?: string;
    mediaUrl?: string;
    imageUrl?: string;
  } | null = null;
  @state() private selectedItem: {
    id: string;
    title?: string;
    mediaUrl?: string;
    imageUrl?: string;

    statistics?: {
      clicks?: number;
      views?: number;
      ctr?: number;
      copyCodeClicks?: number;
      shopClicks?: number;
      clicksProgress?: number;
      viewsProgress?: number;
      ctrProgress?: number;
      copyCodeClicksProgress?: number;
      shopClicksProgress?: number;
    };
  } | null = null;

  @query("#chart") private _chartCanvas!: HTMLCanvasElement;
  @query("#custom-range-form") private _customRangeForm!: HTMLFormElement;
  private _chart?: Chart;
  private _modalChart?: Chart;

  static override styles = [analyticsPageStyles];

  private analyticsQuery = new DataQueryController(this, () =>
    analyticsDataQueryOptions({
      filter: this.activeTab,
      startDate: this.startDate,
      endDate: this.endDate,
    }),
  );

  constructor() {
    super();
    this._applyPreset(7);
  }

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener("popstate", this.handlePopState);
  }

  private _applyPreset(days: number) {
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const start = new Date(end.getTime() - (days - 1) * 86_400_000);
    start.setHours(0, 0, 0, 0);

    this.startDate = start;
    this.endDate = end;
  }

  protected override firstUpdated() {
    this._chart = new Chart(this._chartCanvas, {
      type: "bar",
      data: { labels: [], datasets: [] },
      options: {
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            ticks: {
              precision: 0,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            xAlign: "center",
            yAlign: "bottom",
            displayColors: false,
            caretPadding: 4,
            caretSize: 4,
            cornerRadius: 4,
          },
        },
      },
    });

    this.analyticsQuery.refetch();
  }

  protected override updated(changed: Map<string, unknown>) {
    if (changed.has("activeTab")) this.analyticsQuery.refetch();
    if (changed.has("startDate") || changed.has("endDate")) this.analyticsQuery.refetch();

    // Update chart when linksSubTab changes
    if (changed.has("linksSubTab")) this._updateChart();

    const data = this.analyticsQuery.result?.data;
    if (this._chart && data?.chartData) {
      this._updateChart();
    }
  }

  handlePopState = async () => {
    this.updateStateFromRouter();
    this.requestUpdate();
  };

  updateStateFromRouter() {
    const firstSegment = router.remainingSegments[0];

    this.showSubscriberEmailsModal = firstSegment === "subscriber";
    this.showStatisticsModal = firstSegment === "links";
  }

  private _updateChart() {
    const data = this.analyticsQuery.result?.data;
    if (!this._chart || !data?.chartData) return;

    let chartData = data.chartData;

    // Filter chart data for links sub-tabs
    if (this.activeTab === "links" && data.metaItems) {
      chartData = this._getFilteredChartData(data);
    }

    // Ensure we have valid chart data
    if (!chartData || !chartData.datasets || chartData.datasets.length === 0) {
      // Fallback to original data
      chartData = data.chartData;
    }

    const formattedData = chartData.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: "#FFFFFF",
      borderRadius: 8,
      barThickness: 10,
      maxBarThickness: 10,
    }));

    this._chart.data = {
      labels: chartData.labels,
      datasets: formattedData,
    } as ChartData<"bar", number[], string>;
    this._chart.update();
  }

  private _getFilteredChartData(data: {
    chartData?: { labels: string[]; datasets: Array<{ data: number[]; label?: string; [key: string]: unknown }> };
    metaItems?: Array<{
      id: string;
      statistics?: { views?: number; clicks?: number };
    }>;
  }): { labels: string[]; datasets: Array<{ label: string; data: number[]; [key: string]: unknown }> } {
    if (!data.chartData || !data.chartData.datasets) {
      return { labels: [], datasets: [] };
    }

    // Filter chart datasets based on sub-tab selection
    // API provides: "Profile Link Clicks", "Picture Link Clicks", "Social Link Clicks"
    const filteredDatasets = data.chartData.datasets
      .filter((dataset) => {
        const label = dataset.label || "";
        if (this.linksSubTab === "socials") {
          // Show only social link clicks for socials tab
          return label.includes("Social Link");
        } else {
          // Show profile and picture link clicks for links tab
          return label.includes("Profile Link") || label.includes("Picture Link");
        }
      })
      .map((dataset) => ({
        ...dataset,
        label: dataset.label || "Unknown", // Ensure label is always a string
      }));

    return {
      labels: data.chartData.labels,
      datasets: filteredDatasets,
    };
  }

  private _getFilteredItems(
    metaItems: Array<{
      id: string;
      statistics?: {
        views?: number;
        clicks?: number;
        ctr?: number;
        viewsProgress?: number;
        clicksProgress?: number;
        ctrProgress?: number;
      };
    }>,
  ) {
    return metaItems.filter((item) => {
      if (this.linksSubTab === "socials") {
        return item.id.startsWith("social");
      } else {
        return !item.id.startsWith("social");
      }
    });
  }

  private _calculateFilteredStatistics(
    metaItems: Array<{
      id: string;
      statistics?: {
        views?: number;
        clicks?: number;
        ctr?: number;
        viewsProgress?: number;
        clicksProgress?: number;
        ctrProgress?: number;
      };
    }>,
  ) {
    const filteredItems = this._getFilteredItems(metaItems);

    const totalViews = filteredItems.reduce((sum, item) => sum + (item.statistics?.views || 0), 0);
    const totalClicks = filteredItems.reduce((sum, item) => sum + (item.statistics?.clicks || 0), 0);
    const ctr = totalViews > 0 ? totalClicks / totalViews : 0;

    // Calculate progress (simplified - using average of individual progresses)
    const viewsProgresses = filteredItems.map((item) => item.statistics?.viewsProgress || 0).filter((p) => p !== 0);
    const clicksProgresses = filteredItems.map((item) => item.statistics?.clicksProgress || 0).filter((p) => p !== 0);
    const ctrProgresses = filteredItems.map((item) => item.statistics?.ctrProgress || 0).filter((p) => p !== 0);

    const avgViewsProgress =
      viewsProgresses.length > 0 ? viewsProgresses.reduce((sum, p) => sum + p, 0) / viewsProgresses.length : 0;
    const avgClicksProgress =
      clicksProgresses.length > 0 ? clicksProgresses.reduce((sum, p) => sum + p, 0) / clicksProgresses.length : 0;
    const avgCtrProgress =
      ctrProgresses.length > 0 ? ctrProgresses.reduce((sum, p) => sum + p, 0) / ctrProgresses.length : 0;

    return {
      views: { total: totalViews, progress: avgViewsProgress },
      clicks: { total: totalClicks, progress: avgClicksProgress },
      ctr: { total: ctr, progress: avgCtrProgress },
    };
  }

  private _onTabClick(tab: typeof this.activeTab) {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
      // Reset links sub-tab to default when switching main tabs
      if (tab === "links") {
        this.linksSubTab = "links";
      }
    }
  }

  private _onLinksSubTabClick(subTab: typeof this.linksSubTab) {
    if (this.linksSubTab !== subTab) this.linksSubTab = subTab;
  }

  private _onRangeChange(e: Event) {
    const value = (e.target as HTMLSelectElement).value as RangeKey;

    if (value === "custom-applied") return;

    if (value === "custom") {
      this.isModalOpen = true;
      this.range = "custom";
      return;
    }

    this.range = value;
    this.customAppliedLabel = null;
    this._applyPreset(value);
  }

  private _validateDateRange(): { isValid: boolean; start: Date; end: Date } | null {
    const fd = new FormData(this._customRangeForm);
    const sStr = fd.get("start") as string | null;
    const eStr = fd.get("end") as string | null;
    if (!sStr || !eStr) return null;

    const start = new Date(sStr);
    const end = new Date(eStr);

    return { isValid: end >= start, start, end };
  }

  private _validateDateRangeOnBlur() {
    const result = this._validateDateRange();
    if (!result) return;

    const endInput = this.shadowRoot?.querySelector('wow-input[name="end"]') as
      | (HTMLElement & { setError: (msg: string) => void; clearError: () => void })
      | null;

    if (!result.isValid) {
      if (endInput) {
        endInput.setError("End date must be after start date");
      }
    } else {
      endInput?.clearError();
    }
  }

  private async _confirmCustom() {
    await this.updateComplete;

    const fd = new FormData(this._customRangeForm);
    const sStr = fd.get("start") as string | null;
    const eStr = fd.get("end") as string | null;
    if (!sStr || !eStr) return;

    const result = this._validateDateRange();
    if (!result) return;

    if (!result.isValid) {
      const endInput = this.shadowRoot?.querySelector('wow-input[name="end"]') as
        | (HTMLElement & { setError: (msg: string) => void })
        | null;
      if (endInput) {
        endInput.setError("End date must be after start date");
      }
      return;
    }

    this.customAppliedLabel = `${sStr} → ${eStr}`;

    const { start, end } = result;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    this.startDate = start;
    this.endDate = end;
    await this.updateComplete;

    this.range = "custom-applied";

    this.isModalOpen = false;
  }

  private _onModalClose() {
    if (this.range === "custom") {
      this.range = 7;
    }
    this.isModalOpen = false;
  }

  private _handleSubscriberEmailsModalOpen() {
    this.showSubscriberEmailsModal = true;

    navigateTo("analytics", true, ["subscriber"]);
  }

  private _handleSubscriberEmailsModalClose() {
    this.showSubscriberEmailsModal = false;
    navigateTo("analytics");
  }

  private _toISO(d: Date) {
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60_000);
    return local.toISOString().slice(0, 10);
  }

  private _todayISO = this._toISO(new Date());

  private renderTabs() {
    const tabs = [
      { key: "links", label: "Links" },
      { key: "posts", label: "Posts" },
      { key: "discounts", label: "Discounts" },
    ];
    return html`
      <group-button>
        ${tabs.map(
          (t) => html`
            <wow-button
              variant=${this.activeTab === t.key ? "primary" : "subtle"}
              fullWidth
              @click=${() => this._onTabClick(t.key as "links" | "posts" | "discounts")}
              >${t.label}</wow-button
            >
          `,
        )}
      </group-button>
    `;
  }

  private renderLinksSubTabs() {
    if (this.activeTab !== "links") return "";

    const subTabs = [
      { key: "links", label: "Your Links" },
      { key: "socials", label: "Your Socials" },
    ];

    return html`
      <div class="links-sub-tabs">
        <group-button>
          ${subTabs.map(
            (t) => html`
              <wow-button
                variant=${this.linksSubTab === t.key ? "primary" : "subtle"}
                size="small"
                @click=${() => this._onLinksSubTabClick(t.key as "links" | "socials")}
                >${t.label}</wow-button
              >
            `,
          )}
        </group-button>
      </div>
    `;
  }

  private renderTrendIndicator(progress: number) {
    return html`
      <span class="analytics-growth">
        <span class="${progress > 0 ? "upwards" : progress === 0 ? "idle" : "downwards"}">
          ${Math.abs(progress).toFixed(0)}%
          ${progress !== 0
            ? progress > 0
              ? html`<ph-arrow-up-right></ph-arrow-up-right>`
              : html`<ph-arrow-down-right></ph-arrow-down-right>`
            : ""}
        </span>
      </span>
    `;
  }

  private renderModal() {
    return html`
      <wow-modal
        variant="responsive"
        @close=${this._onModalClose.bind(this)}
        @footer-button-clicked=${() => this._customRangeForm.requestSubmit()}
      >
        <span slot="header">${t("analytics.customRange.title")}</span>

        <form
          id="custom-range-form"
          @submit=${(e: Event) => {
            e.preventDefault();
            this._confirmCustom();
          }}
        >
          <wow-input
            name="start"
            type="date"
            required
            label="Start date"
            max=${this._todayISO}
            .value=${this.startDate ? this._toISO(this.startDate) : ""}
            @input=${() => this._validateDateRangeOnBlur()}
            @blur=${() => this._validateDateRangeOnBlur()}
          ></wow-input>

          <wow-input
            name="end"
            type="date"
            required
            label="End date"
            max=${this._todayISO}
            .value=${this.endDate ? this._toISO(this.endDate) : ""}
            @input=${() => this._validateDateRangeOnBlur()}
            @blur=${() => this._validateDateRangeOnBlur()}
          ></wow-input>
        </form>

        <span slot="footer-button-text"> ${t("analytics.customRange.confirm")} </span>
      </wow-modal>
    `;
  }

  private renderSpecificStatisticsModal() {
    if (!this.selectedItem || !this.showStatisticsModal) return "";
    const handle = appContext.currentUser?.handle;

    const item = this.selectedItem;

    const stats = item.statistics || {};
    switch (this.activeTab) {
      case "links":
        return html`<div class="statistics-metrics">
              
                <div class="analytics-stats">
                  <div class="analytics-card">
                    <span class="analytics-title">Views</span>
                      <div class="analytics-data">
                        <span class="analytics-value">${stats?.views}</span>
                        ${this.renderTrendIndicator(stats?.viewsProgress ?? 0)}
                      </div>
                  </div>

                  <div class="analytics-card">
                   <span class="analytics-title">Clicks</span>
                    <div class="analytics-data">
                      <span class="analytics-value">${stats?.clicks}</span>
                      ${this.renderTrendIndicator(stats?.clicksProgress ?? 0)}
                   </div>

                  </div>
                  <div class="analytics-card">
                        <span class="analytics-title">CTR</span>
                        <div class="analytics-data">
                          <span class="analytics-value">${stats.ctr ? (stats.ctr * 100).toFixed(2) : "0.00"}%</span>
                          ${this.renderTrendIndicator(stats.ctrProgress ?? 0)}
                        </div>
                  </div>
                </div>
                 <div class="analytics-visualization">
              <div class="analytics-card chart">
                ${(() => {
                  const preset = RANGE_PRESETS.find((r) => String(r.key) === String(this.range));
                  if (!preset) return "";
                  if (preset.key === "custom-applied") {
                    return html` <p>Between ${this.customAppliedLabel}</p> `;
                  }
                  return html`<p>${preset.label}</p>`;
                })()}
                <canvas id="modal-chart-${item.id}"></canvas>
              </div>
            </div>
             <section class="link-card" @click=${() => this._handleItemClick(item)}>
                  ${
                    item.imageUrl
                      ? html`<img
                          src=${getAssetUrl(item.imageUrl)}
                          alt="${item.title ?? ""}"
                          class="link-card-icon"
                          style="border-radius: var(--sds-size-radius-400);"
                        />`
                      : html`<div></div>`
                  }

                  <div class="link-card-content">
                    <p class="link-card-title">${item.title}</p>
                    <p class="link-card-url" style="margin: 0;">${item.mediaUrl}</p>
                  </div>
                  <ph-caret-right size="14"></ph-caret-right>
                </section>
          </div>
        </div>
    `;
      case "discounts":
        return html`<div class="statistics-metrics">
          <div class="analytics-stats">
            <div class="analytics-card">
              <span class="analytics-title">Code copied</span>
              <div class="analytics-data">
                <span class="analytics-value">${stats.copyCodeClicks}</span>
                ${this.renderTrendIndicator(stats.copyCodeClicksProgress ?? 0)}
              </div>
            </div>

            <div class="analytics-card">
              <span class="analytics-title">Go to the shop clicks</span>
              <div class="analytics-data">
                <span class="analytics-value">${stats.shopClicks}</span>
                ${this.renderTrendIndicator(stats.shopClicksProgress ?? 0)}
              </div>
            </div>
          </div>
          <div class="analytics-visualization">
            <div class="analytics-card chart">
              ${(() => {
                const preset = RANGE_PRESETS.find((r) => String(r.key) === String(this.range));
                if (!preset) return "";
                if (preset.key === "custom-applied") {
                  return html` <p>Between ${this.customAppliedLabel}</p> `;
                }
                return html`<p>${preset.label}</p>`;
              })()}
              <canvas id="modal-chart-${item.id}"></canvas>
            </div>
            <section class="link-card" @click=${() => window.open(`/${handle}/links`, "_self", "noopener,noreferrer")}>
              ${item.imageUrl
                ? html`<img
                    src=${getAssetUrl(item.imageUrl)}
                    alt="${item.title ?? ""}"
                    class="link-card-icon"
                    style="border-radius: var(--sds-size-radius-400);"
                  />`
                : html`<div></div>`}

              <div class="link-card-content">
                <p class="link-card-title">${item.title}</p>
                <p class="link-card-url" style="margin: 0;">${item.mediaUrl}</p>
              </div>
              <ph-caret-right size="14"></ph-caret-right>
            </section>
          </div>
        </div>`;

      case "posts":
        return html`<div class="statistics-metrics">
              
                <div class="analytics-stats">
                  <div class="analytics-card">
                    <span class="analytics-title">Views</span>
                      <div class="analytics-data">
                        <span class="analytics-value">${stats?.views}</span>
                        ${this.renderTrendIndicator(stats?.viewsProgress ?? 0)}
                      </div>
                  </div>

                  <div class="analytics-card">
                   <span class="analytics-title">Clicks</span>
                    <div class="analytics-data">
                      <span class="analytics-value">${stats?.clicks}</span>
                      ${this.renderTrendIndicator(stats?.clicksProgress ?? 0)}
                   </div>

                  </div>
                  <div class="analytics-card">
                        <span class="analytics-title">CTR</span>
                        <div class="analytics-data">
                          <span class="analytics-value">${stats.ctr ? (stats.ctr * 100).toFixed(2) : "0.00"}%</span>
                          ${this.renderTrendIndicator(stats.ctrProgress ?? 0)}
                        </div>
                  </div>
                </div>
                 <div class="analytics-visualization">
              <div class="analytics-card chart">
                ${(() => {
                  const preset = RANGE_PRESETS.find((r) => String(r.key) === String(this.range));
                  if (!preset) return "";
                  if (preset.key === "custom-applied") {
                    return html` <p>Between ${this.customAppliedLabel}</p> `;
                  }
                  return html`<p>${preset.label}</p>`;
                })()}
                <canvas id="modal-chart-${item.id}"></canvas>
              </div>
            </div>
             <section class="link-card" @click=${() => this._handleItemClick(item)}>
                  ${
                    item.mediaUrl
                      ? item.mediaUrl.match(/\.(mp4|webm|ogg)$/i)
                        ? html`<video src=${getAssetUrl(item.mediaUrl)} class="link-card-icon" muted loop></video>`
                        : html`<img
                            src=${getAssetUrl(item.mediaUrl)}
                            alt="${item.title ?? ""}"
                            class="link-card-icon"
                          />`
                      : html`<div></div>`
                  }

                  <div class="link-card-content">
                    <p class="link-card-title">${item.title}</p>
                  </div>
                  <ph-caret-right size="14"></ph-caret-right>
                </section>
          </div>
        </div>
    `;
      default:
        break;
    }
  }

  private renderStatisticsModal() {
    if (!this.selectedItem || !this.showStatisticsModal) return "";
    const item = this.selectedItem;
    return html`
      <wow-modal variant="responsive" @close=${this._handleStatisticsModalClose.bind(this)}>
        <span slot="header">${item.title}</span>
        ${this.renderSpecificStatisticsModal()}
      </wow-modal>
    `;
  }

  private _handleStatisticsModalClose() {
    this._destroyModalChart();
    this.showStatisticsModal = false;
    this.selectedItem = null;
    navigateTo("analytics");
  }

  private _handleItemClick(item: typeof this.selectedItem) {
    const handle = appContext.currentUser?.handle;
    if (!handle || !item) return;

    if (this.activeTab === "links") {
      // Navigate to profile page with anchor
      window.open(`/${handle}/links`, "_self", "noopener,noreferrer");
    } else if (this.activeTab === "posts") {
      // Open full-screen modal for posts
      this.selectedPost = item;
      this.showFullScreenPostModal = true;
    }
  }

  private _handleFullScreenPostModalClose() {
    this.showFullScreenPostModal = false;
    this.selectedPost = null;
  }

  private renderFullScreenPostModal() {
    if (!this.selectedPost || !this.showFullScreenPostModal) return "";

    const post = this.selectedPost;
    return html`
      <wow-modal
        variant="responsive"
        @close=${this._handleFullScreenPostModalClose.bind(this)}
        style="--wow-modal-width: 100vw; --wow-modal-height: 100vh; --wow-modal-max-width: 100vw; --wow-modal-max-height: 100vh;"
      >
        <span slot="header">${post.title || "Post"}</span>

        <div
          style="display: flex; flex-direction: column; align-items: center; gap: var(--sds-size-space-600); width: 100%; height: 100%;"
        >
          ${post.mediaUrl
            ? post.mediaUrl.match(/\.(mp4|webm|ogg)$/i)
              ? html`<video
                  src=${getAssetUrl(post.mediaUrl)}
                  controls
                  autoplay
                  muted
                  style="max-width: 100%; max-height: 100vh; object-fit: contain;"
                ></video>`
              : html`<img
                  src=${getAssetUrl(post.mediaUrl)}
                  alt="${post.title || "Post content"}"
                  style="max-width: 100%; max-height: 100vh; object-fit: contain;"
                />`
            : html`<div
                style="display: flex; align-items: center; justify-content: center; min-height: 200px; color: var(--sds-color-text-default-secondary);"
              >
                No media content available
              </div>`}
          ${post.title ? html`<h2 style="margin: 0; text-align: center;">${post.title}</h2>` : ""}
        </div>
      </wow-modal>
    `;
  }

  private _destroyModalChart() {
    if (this._modalChart) {
      this._modalChart.destroy();
      this._modalChart = undefined;
    }
  }

  private _createModalChart(item: {
    id: string;
    statistics?: { clicks?: number; views?: number; ctr?: number; copyCodeClicks?: number; shopClicks?: number };
  }) {
    this._destroyModalChart();

    const canvas = this.shadowRoot?.querySelector(`#modal-chart-${item.id}`) as HTMLCanvasElement;
    if (!canvas) return;

    const stats = item.statistics || {};
    const chartData = this._getChartDataForItem(stats);

    this._modalChart = new Chart(canvas, {
      type: chartData.type as "bar",
      data: chartData.data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
          },
          tooltip: {
            displayColors: false,
            cornerRadius: 4,
          },
        },
        ...(chartData.type === "bar"
          ? {
              scales: {
                x: { grid: { display: false } },
                y: {
                  ticks: { precision: 0 },
                  beginAtZero: true,
                },
              },
            }
          : {}),
      },
    });
  }

  private _getChartDataForItem(stats: {
    clicks?: number;
    views?: number;
    ctr?: number;
    copyCodeClicks?: number;
    shopClicks?: number;
  }) {
    if (this.activeTab === "discounts") {
      return {
        type: "bar",
        data: {
          labels: ["Copy Code Clicks", "Go to Shop Clicks"],
          datasets: [
            {
              label: "Action Metrics",
              data: [stats.copyCodeClicks || 0, stats.shopClicks || 0],
              backgroundColor: "#FFFFFF",
              borderRadius: 8,
              barThickness: 10,
              maxBarThickness: 10,
            },
          ],
        },
      };
    } else {
      return {
        type: "bar",
        data: {
          labels: ["Views", "Clicks"],
          datasets: [
            {
              label: "Action Metrics",
              data: [stats.views || 0, stats.clicks || 0],
              backgroundColor: "#FFFFFF",
              borderRadius: 8,
              barThickness: 10,
              maxBarThickness: 10,
            },
          ],
        },
      };
    }
  }

  private _openStatisticsModal(item: {
    id: string;
    title?: string;
    mediaUrl?: string;
    statistics?: { clicks?: number; views?: number; ctr?: number; copyCodeClicks?: number; shopClicks?: number };
  }) {
    this.selectedItem = item;
    this.showStatisticsModal = true;
    navigateTo("analytics", true, ["links", item.id]);
    this.updateComplete.then(() => {
      this._createModalChart(item);
    });
  }

  private renderSpecificStatistics(
    metaItems: Array<{
      id: string;
      title?: string;
      mediaUrl?: string;
      imageUrl?: string;
      statistics?: { clicks?: number; views?: number; ctr?: number; copyCodeClicks?: number; shopClicks?: number };
    }>,
  ) {
    switch (this.activeTab) {
      case "links": {
        // If regular links exist, show sub-tabs; if only social links, show them directly
        const regularLinks = metaItems.filter((item) => !item.id.startsWith("social"));
        const socialLinks = metaItems.filter((item) => item.id.startsWith("social"));

        // Return nothing if no links exist at all
        if (metaItems.length === 0) {
          return nothing;
        }

        // If no regular links but social links exist, show social links directly
        if (regularLinks.length === 0 && socialLinks.length > 0) {
          return html`
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <p>Your Social Links</p>
            </div>
            <div class="link-cards">
              ${socialLinks.map((item) => {
                if (item.title && item.title.startsWith("http")) {
                  const parsedUrl = new URL(item.title ?? "");
                  const hostname = parsedUrl.hostname;
                  const cleanHostname = hostname.replace(/^www\./, "");

                  const platformConfig = platformConfigs.find(
                    (config) => config.domains.includes(cleanHostname) || config.domains.includes(hostname),
                  );
                  return html`
                    <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                      ${platformConfig?.colorfulIcon
                        ? html`<img
                            src=${platformConfig?.colorfulIcon}
                            alt="${platformConfig.platformName ?? ""}"
                            class="link-card-icon"
                          />`
                        : html`<ph-question size="2rem"></ph-question>`}

                      <div class="link-card-content">
                        <p class="link-card-title">${item.title}</p>
                      </div>
                      <ph-caret-right size="14"></ph-caret-right>
                    </section>
                  `;
                }

                const emailConfig = platformConfigs.find((config) => config.platform === "email");

                return html`
                  <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                    ${emailConfig?.colorfulIcon
                      ? html`<img src=${emailConfig?.colorfulIcon} alt="${item.title ?? ""}" class="link-card-icon" />`
                      : html`<ph-question size="2rem"></ph-question>`}

                    <div class="link-card-content">
                      <p class="link-card-title">${item.title}</p>
                    </div>
                    <ph-caret-right size="14"></ph-caret-right>
                  </section>
                `;
              })}
            </div>
          `;
        }

        // If regular links exist, show sub-tabs and filter based on current sub-tab
        const filteredItems = metaItems.filter((item) => {
          if (this.linksSubTab === "socials") {
            return item.id.startsWith("social");
          } else {
            return !item.id.startsWith("social");
          }
        });
        return html`
          ${this.renderLinksSubTabs()}
          <div style="display: flex; align-items: center; justify-content: space-between;"></div>
          <div class="link-cards">
            ${filteredItems.map((item) => {
              if (item.id.startsWith("social") && item.title) {
                if (item.title.startsWith("http")) {
                  const parsedUrl = new URL(item.title ?? "");
                  const hostname = parsedUrl.hostname;
                  const cleanHostname = hostname.replace(/^www\./, "");

                  const platformConfig = platformConfigs.find(
                    (config) => config.domains.includes(cleanHostname) || config.domains.includes(hostname),
                  );
                  return html`
                    <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                      ${platformConfig?.colorfulIcon
                        ? html`<img
                            src=${platformConfig?.colorfulIcon}
                            alt="${platformConfig.platformName ?? ""}"
                            class="link-card-icon"
                          />`
                        : html`<ph-question size="2rem"></ph-question>`}

                      <div class="link-card-content">
                        <p class="link-card-title">${item.title}</p>
                      </div>
                      <ph-caret-right size="14"></ph-caret-right>
                    </section>
                  `;
                }

                const emailConfig = platformConfigs.find((config) => config.platform === "email");

                return html`
                  <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                    ${emailConfig?.colorfulIcon
                      ? html`<img src=${emailConfig?.colorfulIcon} alt="${item.title ?? ""}" class="link-card-icon" />`
                      : html`<ph-question size="2rem"></ph-question>`}

                    <div class="link-card-content">
                      <p class="link-card-title">${item.title}</p>
                    </div>
                    <ph-caret-right size="14"></ph-caret-right>
                  </section>
                `;
              }

              return html`
                <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                  ${item.imageUrl
                    ? html`<img
                        src=${getAssetUrl(item.imageUrl)}
                        alt="${item.title ?? ""}"
                        class="link-card-icon"
                        style="border-radius: var(--sds-size-radius-400);"
                      />`
                    : html`<div></div>`}

                  <div class="link-card-content">
                    <p class="link-card-title">${item.title}</p>
                    <p class="link-card-url" style="margin: 0;">${item.mediaUrl}</p>
                  </div>
                  <ph-caret-right size="14"></ph-caret-right>
                </section>
              `;
            })}
          </div>
        `;
      }
      case "posts":
        // Return nothing if no items to display
        if (metaItems.length === 0) {
          return nothing;
        }

        return html`
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <p>Your Posts</p>
          </div>
          <div class="link-cards">
            ${metaItems.map(
              (item) => html`
                <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                  ${item.mediaUrl
                    ? item.mediaUrl.match(/\.(mp4|webm|ogg)$/i)
                      ? html`<video src=${getAssetUrl(item.mediaUrl)} class="link-card-icon" muted loop></video>`
                      : html`<img src=${getAssetUrl(item.mediaUrl)} alt="${item.title ?? ""}" class="link-card-icon" />`
                    : html`<div></div>`}

                  <div class="link-card-content">
                    <p class="link-card-title">${item.title}</p>
                  </div>
                  <ph-caret-right size="14"></ph-caret-right>
                </section>
              `,
            )}
          </div>
        `;
      case "discounts":
        // Return nothing if no items to display
        if (metaItems.length === 0) {
          return nothing;
        }

        return html`
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <p>Your Discounts</p>
          </div>
          <div class="link-cards">
            ${metaItems.map((item) => {
              return html`
                <section class="link-card" @click=${() => this._openStatisticsModal(item)}>
                  ${item.imageUrl
                    ? html`<img
                        src=${getAssetUrl(item.imageUrl)}
                        alt="${item.title ?? ""}"
                        class="link-card-icon"
                        style="border-radius: var(--sds-size-radius-400);"
                      />`
                    : html`<div></div>`}

                  <div class="link-card-content">
                    <p class="link-card-title">${item.title}</p>
                    <p class="link-card-url" style="margin: 0;">${item.mediaUrl}</p>
                  </div>
                  <ph-caret-right size="14"></ph-caret-right>
                </section>
              `;
            })}
          </div>
        `;

      default:
        break;
    }
  }

  protected override render() {
    const data = this.analyticsQuery.result?.data;
    return html`
      <page-content pageTitle=${t("pages.analytics")}>
        <div style="overflow: auto; padding-right: 10px;">
          <div class="analytics-filters">
            ${this.renderTabs()}
            <select @change=${this._onRangeChange} .value=${String(this.range)}>
              ${RANGE_PRESETS.map((r) =>
                r.key !== "custom-applied" ? html`<option value=${r.key}>${r.label}</option>` : "",
              )}
              ${this.customAppliedLabel
                ? html`<option value="custom-applied">${this.customAppliedLabel}</option>`
                : null}
            </select>
          </div>

          <div class="analytics-overview">
            <div class="analytics-cards">
              ${data?.subscribers
                ? html`
                    <div class="analytics-card subscribers-card" @click=${this._handleSubscriberEmailsModalOpen}>
                      <div class="analytics-subscribers">
                        <span class="analytics-title">${t("analytics.followers")}</span>
                        <button class="subscribers-button">
                          <span>View</span>
                          <ph-caret-right size="14"></ph-caret-right>
                        </button>
                      </div>

                      <div class="analytics-data">
                        <span class="analytics-value">${data.subscribers?.total || 0}</span>
                        ${this.renderTrendIndicator(data.subscribers?.progress || 0)}
                      </div>
                    </div>
                  `
                : ""}
              ${this.activeTab === "discounts"
                ? html` <div class="analytics-stats">
                    <div class="analytics-card">
                      <span class="analytics-title">Code copied</span>
                      <div class="analytics-data">
                        <span class="analytics-value">${data?.copyCodeClicks?.total ?? 0}</span>
                        ${this.renderTrendIndicator(data?.copyCodeClicks?.progress ?? 0)}
                      </div>
                    </div>

                    <div class="analytics-card">
                      <span class="analytics-title">Go to the shop clicks</span>
                      <div class="analytics-data">
                        <span class="analytics-value">${data?.shopClicks?.total ?? 0}</span>
                        ${this.renderTrendIndicator(data?.shopClicks?.progress ?? 0)}
                      </div>
                    </div>
                  </div>`
                : html`<div class="analytics-stats">
              ${(() => {
                // For links tab, check if we should use filtered statistics
                if (this.activeTab === "links" && data?.metaItems) {
                  // Check if any items actually have statistics data
                  const hasItemStatistics = data.metaItems.some(
                    (item) => item.statistics && (item.statistics.views || item.statistics.clicks),
                  );

                  // Only use filtered stats if items have individual statistics
                  if (hasItemStatistics) {
                    const filteredStats = this._calculateFilteredStatistics(data.metaItems);
                    const hasFilteredData = filteredStats.views.total > 0 || filteredStats.clicks.total > 0;

                    if (hasFilteredData) {
                      return html`
                        <div class="analytics-card">
                          <span class="analytics-title">Views</span>
                          <div class="analytics-data">
                            <span class="analytics-value">${data.views.total}</span>
                            ${this.renderTrendIndicator(data.views.progress)}
                          </div>
                        </div>

                        <div class="analytics-card">
                          <span class="analytics-title">Clicks</span>
                          <div class="analytics-data">
                            <span class="analytics-value">${filteredStats.clicks.total}</span>
                            ${this.renderTrendIndicator(filteredStats.clicks.progress)}
                          </div>
                        </div>

                        <div class="analytics-card">
                          <span class="analytics-title">CTR</span>
                          <div class="analytics-data">
                            <span class="analytics-value">${(filteredStats.ctr.total * 100).toFixed(2)}%</span>
                            ${this.renderTrendIndicator(filteredStats.ctr.progress)}
                          </div>
                        </div>
                      `;
                    }
                  }
                }

                // Default statistics for posts tab or fallback for links tab
                return html`
                  <div class="analytics-card">
                    <span class="analytics-title">Views</span>
                    <div class="analytics-data">
                      <span class="analytics-value">${data?.views?.total ?? 0}</span>
                      ${this.renderTrendIndicator(data?.views?.progress ?? 0)}
                    </div>
                  </div>

                  <div class="analytics-card">
                    <span class="analytics-title">Clicks</span>
                    <div class="analytics-data">
                      <span class="analytics-value">${data?.clicks?.total ?? 0}</span>
                      ${this.renderTrendIndicator(data?.clicks?.progress ?? 0)}
                    </div>
                  </div>

                  <div class="analytics-card">
                    <span class="analytics-title">CTR</span>
                    <div class="analytics-data">
                      <span class="analytics-value"
                        >${data?.ctr?.total ? (data.ctr.total * 100).toFixed(2) : "0.00"}%</span
                      >
                      ${this.renderTrendIndicator(data?.ctr?.progress ?? 0)}
                    </div>
                  </div>
                `;
              })()}
            </div>
          </div>`}
            </div>

            <div class="analytics-visualization">
              <div class="analytics-card chart">
                <canvas id="chart"></canvas>
              </div>
            </div>
          </div>

          <!-- Links, Posts and Discounts statistics section -->

          ${data?.metaItems ? html` ${this.renderSpecificStatistics(data?.metaItems)} ` : ""}

          <!-- Add gap when content is empty -->
          ${!data?.metaItems ||
          (this.activeTab === "links" && data.metaItems.length === 0) ||
          (this.activeTab === "posts" && data.metaItems.length === 0) ||
          (this.activeTab === "discounts" && data.metaItems.length === 0)
            ? html`<div style="margin-top: var(--sds-size-space-800);"></div>`
            : nothing}

          <div class="analytics-card">
            <span class="analytics-title">In need of more insights?</span>
            <div class="analytics-data">
              <span class="analytics-value">support@worldofwow.com</span>
            </div>
          </div>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.showSubscriberEmailsModal && data?.subscribers
          ? html` <subscriber-emails-modal
              .open=${this.showSubscriberEmailsModal}
              .emails=${data.subscribers?.emails || []}
              total=${data.subscribers?.total || 0}
              @modal-closed=${this._handleSubscriberEmailsModalClose}
            >
            </subscriber-emails-modal>`
          : ""}
        ${this.renderStatisticsModal()} ${this.renderFullScreenPostModal()}
      </page-content>
    `;
  }
}
